'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { ImageType } from '@eliadmin/core/src/api/api-types';
/* eslint-disable @typescript-eslint/no-unused-vars */
import { fetchDynamicSections } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { Container } from '@eliadmin/core/src/components/ui/container';
import type { HomePageProps } from '@eliadmin/core/src/pages/homepage/page';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { useState } from 'react';

type CustomItemType = { image: ImageType; name: string };

export function CustomHomepage(props: HomePageProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const { isOpen, setIsOpen } = useDisclosure();
  const [lightboxImages, setLightboxImages] = useState<CustomItemType[]>([]);

  const { data: items, isLoading } = useQuery({
    queryKey: ['homepage-images'],
    queryFn: () =>
      fetchDynamicSections({ locale, sectionIds: [1], query: { results: 99 } }).then((data: any) => {
        const transformedImages = data?.map((item: any) => ({
          name: item.fields?.[0]?.value as string,
          image: item.fields?.[1]?.image as ImageType,
        }));
        setLightboxImages(transformedImages);
        return transformedImages;
      }),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const images = items ?? [];

  const handleOpenLightbox = (index: number) => {
    const rotated = [...images.slice(index), ...images.slice(0, index)];
    setLightboxImages(rotated);
    setIsOpen(true);
  };

  return (
    <CarouselProvider>
      <section className={cn('my-8 w-[100vw]', 'ea-blog-news__container')}>
        <Container>
          <h2 className="mb-3 text-left text-2xl font-bold">Naše realizácie</h2>
        </Container>
        <LightboxComponent
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          images={lightboxImages
            .map((item: CustomItemType) => {
              if (!item.image?.url) return null;

              return {
                url: item.image?.url,
                alt: item.name,
              };
            })
            .filter(Boolean)}
          zoom={{ maxZoomPixelRatio: 8 }}
        />

        <Carousel
          className={{
            wrapper: cn('w-full', 'ea-blog-news__carousel-wrapper'),
            slide: cn('mr-4 h-full w-auto max-w-[90vw] p-2', 'ea-blog-news__carousel-slide'),
          }}
          options={{ slidesToScroll: 1, dragFree: true }}
          slides={images.map((item: CustomItemType, index: number) => {
            if (!item.image?.url) return null;
            return (
              <button
                key={index}
                type="button"
                className="block w-full text-left"
                aria-label={item.name ? `Otvoriť realizáciu: ${item.name}` : 'Otvoriť realizáciu'}
                onClick={() => {
                  handleOpenLightbox(index);
                }}
              >
                <ImageCard name={item.name} image={item.image} />
              </button>
            );
          })}
          enableLoop
          enableInterval
          intervalLength={8000}
        />
      </section>
    </CarouselProvider>
  );
}

const ImageCard = ({ image, name }: CustomItemType) => {
  return (
    <div className="relative flex h-[360px] max-w-[25vw] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-lg">
      <Image
        src={image?.url ?? ''}
        alt={resolveImageAlt(image, name)}
        width={0}
        height={0}
        sizes="25vw"
        className="h-full w-full select-none object-cover"
      />
      {name && name.length && (
        <h3
          className="absolute bottom-0 left-0 z-10 h-10 px-4 py-2 text-lg font-semibold text-primary"
          style={{ backgroundColor: '#ffdcbc', borderTopRightRadius: '16px' }}
        >
          {name}
        </h3>
      )}
    </div>
  );
};
